import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

// ===== EAGERLY LOADED (critical path) =====
// Landing page is the unauth entry point — must render on first paint.
// Layout is tiny and used by every /app/* route, no benefit to lazy.
// ScoresRoutes is the iOS app's landing → keep eager so Capacitor
// users don't see a Suspense flash on cold start.
import Layout from "./components/Layout";
import LandingPage from "./pages/LandingPage";
import ScoresRoutes from "./scores/ScoresRoutes";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import NotificationPrompt from "./components/NotificationPrompt";
import FallbackDomainBanner from "./components/FallbackDomainBanner";

// ===== LAZILY LOADED (route-level code-splitting) =====
// Cut initial bundle from ~384KB → ~150KB gzipped by deferring everything
// users don't need on the landing screen. Each page becomes its own chunk
// that streams in when the user actually visits that route.
const Home = lazy(() => import("./pages/Home"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Picks = lazy(() => import("./pages/Picks"));
const Results = lazy(() => import("./pages/Results"));
const Profile = lazy(() => import("./pages/Profile"));
const Subscription = lazy(() => import("./pages/Subscription"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const FreeTrial = lazy(() => import("./pages/FreeTrial"));
const Partners = lazy(() => import("./pages/Partners"));
const Top10 = lazy(() => import("./pages/Top10"));
const News = lazy(() => import("./pages/News"));
const Admin = lazy(() => import("./pages/Admin"));
const ContentEngine = lazy(() => import("./pages/ContentEngine"));
const GrowthEngine = lazy(() => import("./pages/GrowthEngine"));
const Acquisition = lazy(() => import("./pages/Acquisition"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const VIPPicks = lazy(() => import("./pages/VIPPicks"));
const ValueBets = lazy(() => import("./pages/ValueBets"));
const Bankers = lazy(() => import("./pages/Bankers"));
const OddsBoard = lazy(() => import("./pages/OddsBoard"));
const BetBuilders = lazy(() => import("./pages/BetBuilders"));
const LadderChallenge = lazy(() => import("./pages/LadderChallenge"));
const Favorites = lazy(() => import("./pages/Favorites"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const LiveScores = lazy(() => import("./pages/LiveScores"));
const FreePreview = lazy(() => import("./pages/FreePreview"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Accuracy = lazy(() => import("./pages/Accuracy"));
const PublicResults = lazy(() => import("./pages/PublicResults"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ReferralCapture = lazy(() => import("./pages/ReferralCapture"));
const Refer = lazy(() => import("./pages/Refer"));

import "./scores/theme.css";
import "./App.css";
import { bootstrapAnalytics } from "./utils/analytics";
import { captureRefFromUrl } from "./utils/referral";
import { StickyBottomBar } from "./components/StickyBottomBar";
import { ExitIntentPopup } from "./components/ExitIntentPopup";
import "./i18n";
import { resolveLanguageFromGeo } from "./i18n";

// Native-iOS-aware home redirect. When running inside the Capacitor
// shell (the iOS/Android app), opening the app should take users
// straight to the Live Scores tab — that's the daily-use feature.
// On web (smartkick.co.uk), keep the marketing landing page intact.
const NativeAwareHome = () => {
  const isNative = typeof window !== 'undefined'
    && window.Capacitor
    && typeof window.Capacitor.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform();
  return isNative ? <Navigate to="/live-scores" replace /> : <LandingPage />;
};

// Tiny full-screen spinner shown while a lazy route's chunk is being
// fetched. Keep it visually neutral so it blends with both the dark
// app theme and the marketing site.
const RouteFallback = () => (
  <div className="min-h-screen bg-[#09090b] flex items-center justify-center" data-testid="route-suspense-fallback">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-500" />
  </div>
);

const ProtectedRoute = ({ children, allowFree = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Redirect free users to subscription page (unless they're admin or on allowed pages)
  if (!allowFree && user.subscription_status === 'free' && !user.is_admin) {
    if (location.pathname !== '/app/subscription' && location.pathname !== '/app/subscription/success') {
      return <Navigate to="/app/subscription" replace />;
    }
  }

  return children;
};

function App() {
  useEffect(() => {
    bootstrapAnalytics();
    resolveLanguageFromGeo();
    captureRefFromUrl();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <FallbackDomainBanner />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Landing page as main entry point */}
                <Route path="/" element={<NativeAwareHome />} />
                <Route path="/welcome" element={<LandingPage />} />

                {/* Marketing pages */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/testimonials" element={<Testimonials />} />

                {/* Free preview - public */}
                <Route path="/free-preview" element={<FreePreview />} />

                {/* Free live scores — the blue Scores experience, mounted at /live-scores/* */}
                <Route path="/live-scores/*" element={<ScoresRoutes basePath="/live-scores" />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/accuracy" element={<Accuracy />} />
                <Route path="/results" element={<PublicResults />} />
                <Route path="/r/:code" element={<ReferralCapture />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/free-trial" element={<FreeTrial />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/affiliates" element={<Partners />} />
                <Route path="/news" element={<News />} />

                {/* Payment success - NO auth required (account created after payment) */}
                <Route path="/app/subscription/success" element={<SubscriptionSuccess />} />

                {/* Admin route */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <Admin />
                  </ProtectedRoute>
                } />
                <Route path="/content-engine" element={
                  <ProtectedRoute>
                    <ContentEngine />
                  </ProtectedRoute>
                } />
                <Route path="/growth-engine" element={
                  <ProtectedRoute>
                    <GrowthEngine />
                  </ProtectedRoute>
                } />
                <Route path="/acquisition" element={
                  <ProtectedRoute>
                    <Acquisition />
                  </ProtectedRoute>
                } />

                {/* App routes with Layout */}
                <Route path="/app" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="match/:matchId" element={<MatchDetail />} />
                  <Route path="picks" element={<Picks />} />
                  <Route path="bankers" element={<Bankers />} />
                  <Route path="odds" element={<OddsBoard />} />
                  <Route path="value-bets" element={<ValueBets />} />
                  <Route path="vip-picks" element={<VIPPicks />} />
                  <Route path="bet-builders" element={<BetBuilders />} />
                  <Route path="ladder" element={<LadderChallenge />} />
                  <Route path="top10" element={<Top10 />} />
                  <Route path="daily-10" element={<Top10 />} />
                  <Route path="favorites" element={<Favorites />} />
                  <Route path="live-scores" element={<LiveScores />} />
                  <Route path="partner-dashboard" element={
                    <ProtectedRoute allowFree>
                      <PartnerDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                  <Route path="terms" element={<TermsOfService />} />
                  <Route path="results" element={
                    <ProtectedRoute>
                      <Results />
                    </ProtectedRoute>
                  } />
                  <Route path="profile" element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } />
                  <Route path="refer" element={
                    <ProtectedRoute allowFree>
                      <Refer />
                    </ProtectedRoute>
                  } />
                  <Route path="subscription" element={<Subscription />} />
                </Route>
              </Routes>
            </Suspense>
            <StickyBottomBar />
            <ExitIntentPopup />
            <Toaster position="top-center" richColors />
            <CookieConsent />
            <NotificationPrompt />
          </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
